import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  OnInit,
  Output,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Option } from '@interfaces/common/option';
import { MeetingLinkSelection } from '@interfaces/meetings/requests/meeting.request';
import {
  MeetingLinkBankRelationshipOptionDto,
  MeetingLinkDealOptionDto,
  MeetingLinkPipelineDealOptionDto,
} from '@interfaces/meetings/responses/meeting.response';
import {
  PARTNER_DEAL_STAGE_LABELS,
  PartnerDealStage,
} from '@interfaces/partners/responses/partner-deal.response';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { MeetingsHttpService } from '@services/http/meetings/meetings-http.service';
import { SharedModule } from '@src/app/shared/shared.module';
import { Subscription, first } from 'rxjs';
import {
  mensagemDoErro,
  rotuloDoDealDoPipeline,
} from '@features/dashboard/meetings/components/meeting-presenter';

/**
 * Campos do vínculo manual em cascata (card 3138), usados pelo "Vincular" e pelo "Adicionar bot na
 * call": **Empresa → Parceiro → Deal**, **Empresa → Deal do Pipeline de crédito** (Adendo 2 do PBI 3225)
 * e, em separado, **Relacionamento bancário → Contato**.
 *
 * Tudo depende da empresa: escolhida, `GET meetings/link-options?companyId=` traz os parceiros
 * ligados a ela, os deals ativos e os relacionamentos bancários com os contatos (decisão do Breno:
 * o banco é filtrado pela empresa). O deal filtra pelo parceiro escolhido e o contato pelo
 * relacionamento — filtro de exibição; quem confere a combinação é o backend.
 */
@Component({
  selector: 'app-meeting-link-fields',
  standalone: true,
  imports: [CommonModule, FormsModule, SharedModule],
  templateUrl: './meeting-link-fields.component.html',
  styleUrl: './meeting-link-fields.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeetingLinkFieldsComponent implements OnInit {
  /** A escolha atual, a cada mudança. */
  @Output() selecaoChange = new EventEmitter<MeetingLinkSelection>();

  protected readonly empresas = signal<Option[]>([]);
  protected readonly parceiros = signal<Option[]>([]);
  protected readonly carregandoEmpresas = signal(false);
  protected readonly carregandoDaEmpresa = signal(false);

  private readonly deals = signal<MeetingLinkDealOptionDto[]>([]);
  private readonly relacionamentos = signal<MeetingLinkBankRelationshipOptionDto[]>([]);
  private readonly dealsDoPipeline = signal<MeetingLinkPipelineDealOptionDto[]>([]);
  private readonly parceiroEscolhido = signal<string | null>(null);
  private readonly relacionamentoEscolhido = signal<string | null>(null);

  /** Deals da empresa; com parceiro escolhido, só os dele. */
  protected readonly opcoesDeDeal = computed<Option[]>(() => {
    const parceiro = this.parceiroEscolhido();
    return this.deals()
      .filter(d => !parceiro || d.partnerId === parceiro)
      .map(d => ({
        label: `#${d.sequentialId} · ${PARTNER_DEAL_STAGE_LABELS[d.stage as PartnerDealStage] ?? d.stage}`,
        value: d.id,
      }));
  });

  /** Deals do Pipeline de crédito da empresa: "#1234 · Referência · Pipeline/Etapa · Banco". */
  protected readonly opcoesDeDealDoPipeline = computed<Option[]>(() =>
    this.dealsDoPipeline().map(d => ({ label: rotuloDoDealDoPipeline(d), value: d.id }))
  );

  protected readonly opcoesDeRelacionamento = computed<Option[]>(() =>
    this.relacionamentos().map(r => ({
      label: r.financialInstitutionName ?? 'Banco',
      value: r.id,
    }))
  );

  /** Contatos do relacionamento escolhido. */
  protected readonly opcoesDeContato = computed<Option[]>(() => {
    const escolhido = this.relacionamentos().find(r => r.id === this.relacionamentoEscolhido());
    return (escolhido?.contacts ?? []).map(c => ({
      label: c.email ? `${c.name ?? c.email} · ${c.email}` : (c.name ?? '—'),
      value: c.id,
    }));
  });

  /** Bindings (ngModel). */
  protected empresa: string | null = null;
  protected parceiro: string | null = null;
  protected deal: string | null = null;
  protected dealDoPipeline: string | null = null;
  protected relacionamento: string | null = null;
  protected contato: string | null = null;

  private readonly destroyRef = inject(DestroyRef);
  private cargaDaEmpresa?: Subscription;

  constructor(
    private readonly meetingsService: MeetingsHttpService,
    private readonly toast: ToastHelperService
  ) {}

  ngOnInit(): void {
    this.carregandoEmpresas.set(true);
    this.meetingsService
      .getLinkOptions()
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: opcoes => {
          this.carregandoEmpresas.set(false);
          this.empresas.set((opcoes?.companies ?? []).map(c => ({ label: c.name, value: c.id })));
        },
        error: erro => {
          this.carregandoEmpresas.set(false);
          this.toast.showToastError(
            'Não foi possível carregar as empresas',
            mensagemDoErro(erro, 'Tente de novo em alguns instantes.')
          );
        },
      });
  }

  /** Limpa a escolha (a modal chama ao abrir). */
  reiniciar(): void {
    this.empresa = null;
    this.limparDaEmpresa();
    this.emitir();
  }

  protected aoEscolherEmpresa(empresa: string | null): void {
    this.empresa = empresa || null;
    this.limparDaEmpresa();
    this.emitir();
    if (!this.empresa) return;

    this.carregandoDaEmpresa.set(true);
    this.cargaDaEmpresa = this.meetingsService
      .getLinkOptions(this.empresa)
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: opcoes => {
          this.carregandoDaEmpresa.set(false);
          this.parceiros.set(
            (opcoes?.partners ?? []).map(p => ({ label: p.name ?? 'Parceiro', value: p.id }))
          );
          this.deals.set(opcoes?.deals ?? []);
          this.relacionamentos.set(opcoes?.bankRelationships ?? []);
          this.dealsDoPipeline.set(opcoes?.pipelineDeals ?? []);
        },
        error: erro => {
          this.carregandoDaEmpresa.set(false);
          this.toast.showToastError(
            'Não foi possível carregar as opções da empresa',
            mensagemDoErro(erro, 'Tente de novo em alguns instantes.')
          );
        },
      });
  }

  protected aoEscolherParceiro(parceiro: string | null): void {
    this.parceiro = parceiro || null;
    this.parceiroEscolhido.set(this.parceiro);
    // O deal escolhido pode não ser do parceiro novo.
    if (this.deal && !this.opcoesDeDeal().some(o => o.value === this.deal)) this.deal = null;
    this.emitir();
  }

  protected aoEscolherDeal(deal: string | null): void {
    this.deal = deal || null;
    this.emitir();
  }

  protected aoEscolherDealDoPipeline(deal: string | null): void {
    this.dealDoPipeline = deal || null;
    this.emitir();
  }

  protected aoEscolherRelacionamento(relacionamento: string | null): void {
    this.relacionamento = relacionamento || null;
    this.relacionamentoEscolhido.set(this.relacionamento);
    this.contato = null;
    this.emitir();
  }

  protected aoEscolherContato(contato: string | null): void {
    this.contato = contato || null;
    this.emitir();
  }

  private limparDaEmpresa(): void {
    this.cargaDaEmpresa?.unsubscribe();
    this.carregandoDaEmpresa.set(false);
    this.parceiro = null;
    this.deal = null;
    this.dealDoPipeline = null;
    this.relacionamento = null;
    this.contato = null;
    this.parceiroEscolhido.set(null);
    this.relacionamentoEscolhido.set(null);
    this.parceiros.set([]);
    this.deals.set([]);
    this.relacionamentos.set([]);
    this.dealsDoPipeline.set([]);
  }

  private emitir(): void {
    this.selecaoChange.emit({
      companyId: this.empresa,
      partnerId: this.parceiro,
      dealId: this.deal,
      bankRelationshipId: this.relacionamento,
      bankManagerId: this.contato,
      pipelineDealId: this.dealDoPipeline,
    });
  }
}
